import * as graphics from "@client/app/graphics";
import {getProfile} from "@client/features/orm";
import {paramsToQueryString} from "@client/gotterdammerung";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import {useSiteContext} from "@client/site/context";
import {useAppSelector} from "@client/store";
import {ArrowLeftIcon, PlusIcon} from "@heroicons/react/24/outline";
import {reverse} from "@reactivated";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import {match} from "ts-pattern";

import peptideData from "../../server/peptides.json";

type PeptideKey = keyof typeof peptideData.peptides;
type BlendKey = keyof typeof peptideData.blends;

export const PeptideDetail = router.PeptideDetail.useConditions((props) => {
    const profile = useAppSelector(getProfile);
    const slug = props.params.slug;
    const peptide = match(slug)
        .when(
            (s) => s in peptideData.peptides,
            (s) => peptideData.peptides[s as PeptideKey],
        )
        .when(
            (s) => s in peptideData.blends,
            (s) => peptideData.blends[s as BlendKey],
        )
        .otherwise(() => null);

    if (peptide == null) {
        throw new Error(`Peptide not found: ${slug}`);
    }

    React.useEffect(() => {
        analytics.register({peptide_slug: slug});
        void analytics.track("PEPTIDES_GUIDE_VIEWED", {
            peptide_slug: slug,
        });

        return () => {
            analytics.unregister("peptide_slug");
        };
    }, [slug]);

    const {request} = useSiteContext();
    const origin = new URL(request.url).origin;
    const detailDesc =
        peptide.detail != null ? peptide.detail.description : peptide.description;
    const metaDescription = `${peptide.name} peptide guide: ${detailDesc}`.slice(
        0,
        160,
    );
    const canonicalUrl = `${origin}${reverse("peptide_guide", {slug})}`;

    return {
        options: {
            title: `${peptide.name} Peptide Guide: Dosage, Side Effects & Storage`,
            scene: {kind: "SIMPLE", nav: profile != null ? "menu" : "none"},
            respectDarkMode: true,
            titleSection:
                profile != null ? (
                    <div className="flex w-full items-center justify-center gap-3 py-1.5">
                        <graphics.Logo className="h-5 w-auto shrink-0 dark:invert" />
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <h1 className="truncate text-xl font-light text-gray-600 dark:text-slate-300">
                            {peptide.name}
                        </h1>
                    </div>
                ) : (
                    <div className="flex w-full items-center justify-center gap-3">
                        <graphics.Logo className="h-5 w-auto shrink-0 dark:invert" />
                        <span className="text-gray-300 dark:text-slate-600">|</span>
                        <h1 className="truncate text-xl font-light text-gray-600 dark:text-slate-300">
                            {peptide.name}
                        </h1>
                    </div>
                ),
            toolbarYouAreInChargeOfTransparency: (
                <div className="flex justify-center pt-3">
                    <router.Link
                        locator={router.locator({
                            route: "peptides.calculator",
                            params: {},
                            queryParams: paramsToQueryString({peptide: slug}),
                        })}
                        className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400"
                    >
                        <ArrowLeftIcon className="size-4" />
                        Back to calculator
                    </router.Link>
                </div>
            ),
        },
        actions: [],
        view: (
            <>
                <link rel="canonical" href={canonicalUrl} />
                <meta name="description" content={metaDescription} />
                <meta
                    property="og:title"
                    content={`${peptide.name} Peptide Guide: Dosage, Side Effects & Storage | Joy Health Tracker`}
                />
                <meta property="og:description" content={metaDescription} />
                <meta
                    property="og:image"
                    content={`${origin}${reverse("peptide_open_graph", {slug})}`}
                />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={canonicalUrl} />
                <article className="space-y-3">
                    {peptide.detail != null && (
                        <div className="relative overflow-hidden rounded-2xl bg-linear-to-br from-indigo-600 via-indigo-700 to-purple-800 p-6 dark:from-indigo-950 dark:via-slate-900 dark:to-slate-900">
                            <div className="absolute -top-8 -right-8 size-36 rounded-full bg-indigo-500/20 dark:bg-indigo-700/20" />
                            <p className="relative text-xs font-semibold tracking-[0.2em] text-indigo-200/80 uppercase dark:text-indigo-400/80">
                                {peptide.detail.category}
                            </p>
                            <h2 className="relative mt-2 text-3xl font-light text-white">
                                {peptide.name}
                            </h2>
                            <div className="relative mt-5 flex divide-x divide-white/20 rounded-xl bg-white/10 py-3 text-center dark:bg-white/5">
                                {peptide.detail.blocks.map((block) => (
                                    <div
                                        key={block.label}
                                        className="flex flex-1 flex-col items-center gap-0.5 px-2"
                                    >
                                        <div className="text-sm text-indigo-200/80">
                                            {block.label}
                                        </div>
                                        <div className="text-base font-light text-white">
                                            {block.value}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {peptide.detail?.guide != null ? (
                        <>
                            <details className="group rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 outline-solid dark:bg-slate-800 dark:outline-slate-600">
                                <summary className="flex cursor-pointer list-none items-center justify-between p-6 [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        About {peptide.name}
                                    </h3>
                                    <PlusIcon className="size-5 text-gray-400 transition-transform group-open:rotate-45 dark:text-slate-500" />
                                </summary>
                                <div className="px-6 pb-6">
                                    <ReactMarkdown className="space-y-3 text-gray-800 dark:text-slate-400 [&_p]:leading-relaxed">
                                        {peptide.detail.guide.description}
                                    </ReactMarkdown>
                                </div>
                            </details>
                            <details className="group rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 outline-solid dark:bg-slate-800 dark:outline-slate-600">
                                <summary className="flex cursor-pointer list-none items-center justify-between p-6 [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        Onset and what to expect
                                    </h3>
                                    <PlusIcon className="size-5 text-gray-400 transition-transform group-open:rotate-45 dark:text-slate-500" />
                                </summary>
                                <div className="px-6 pb-6">
                                    <ReactMarkdown className="space-y-3 text-gray-600 dark:text-slate-400 [&_p]:leading-relaxed">
                                        {peptide.detail.guide.onset}
                                    </ReactMarkdown>
                                </div>
                            </details>
                            <details className="group rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 outline-solid dark:bg-slate-800 dark:outline-slate-600">
                                <summary className="flex cursor-pointer list-none items-center justify-between p-6 [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        Side effects
                                    </h3>
                                    <PlusIcon className="size-5 text-gray-400 transition-transform group-open:rotate-45 dark:text-slate-500" />
                                </summary>
                                <div className="px-6 pb-6">
                                    <ReactMarkdown className="space-y-3 text-gray-600 dark:text-slate-400 [&_p]:leading-relaxed">
                                        {peptide.detail.guide.sideEffects}
                                    </ReactMarkdown>
                                </div>
                            </details>
                            <details className="group rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 outline-solid dark:bg-slate-800 dark:outline-slate-600">
                                <summary className="flex cursor-pointer list-none items-center justify-between p-6 [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        Storage and reconstitution
                                    </h3>
                                    <PlusIcon className="size-5 text-gray-400 transition-transform group-open:rotate-45 dark:text-slate-500" />
                                </summary>
                                <div className="px-6 pb-6">
                                    <ReactMarkdown className="space-y-3 text-gray-600 dark:text-slate-400 [&_p]:leading-relaxed">
                                        {peptide.detail.guide.storage}
                                    </ReactMarkdown>
                                </div>
                            </details>
                            <details className="group rounded-xl bg-white outline-1 -outline-offset-1 outline-gray-300 outline-solid dark:bg-slate-800 dark:outline-slate-600">
                                <summary className="flex cursor-pointer list-none items-center justify-between p-6 [&::-webkit-details-marker]:hidden">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">
                                        Also known as
                                    </h3>
                                    <PlusIcon className="size-5 text-gray-400 transition-transform group-open:rotate-45 dark:text-slate-500" />
                                </summary>
                                <div className="px-6 pb-6">
                                    <p className="leading-relaxed text-gray-600 dark:text-slate-400">
                                        {peptide.detail.guide.aliases}
                                    </p>
                                </div>
                            </details>
                        </>
                    ) : null}
                </article>
            </>
        ),
    };
});
